import React, { useEffect, useState } from 'react';
import { accountsApi, Account, getErrorMessage } from '../services/api';
import { useToast } from '../components/Toast';

const s = {
  page: { padding: '30px 40px', color: '#e2e8f0', minHeight: '100vh' } as React.CSSProperties,
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 30 } as React.CSSProperties,
  title: { fontSize: 28, fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: 12 } as React.CSSProperties,
  btnPrimary: { background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)', color: '#fff', border: 'none', padding: '12px 24px', borderRadius: 10, cursor: 'pointer', fontWeight: 600, fontSize: 14 } as React.CSSProperties,
  btnDanger: { background: 'linear-gradient(135deg, #ef4444, #dc2626)', color: '#fff', border: 'none', padding: '8px 18px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 } as React.CSSProperties,
  btnActivate: { background: 'linear-gradient(135deg, #10b981, #059669)', color: '#fff', border: 'none', padding: '8px 18px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 } as React.CSSProperties,
  btnSecondary: { background: '#1e293b', color: '#94a3b8', border: '1px solid #334155', padding: '8px 18px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 } as React.CSSProperties,
  btnEdit: { background: '#1e293b', color: '#f59e0b', border: '1px solid #f59e0b44', padding: '8px 18px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 } as React.CSSProperties,
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(340px, 100%), 1fr))', gap: 20 } as React.CSSProperties,
  card: { background: '#0f172a', border: '1px solid #1e293b', borderRadius: 14, padding: 24, position: 'relative' as const, overflow: 'hidden' } as React.CSSProperties,
  cardActive: { border: '1px solid #8b5cf6', boxShadow: '0 0 20px rgba(139, 92, 246, 0.15)' } as React.CSSProperties,
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 } as React.CSSProperties,
  accName: { fontSize: 20, fontWeight: 700, color: '#f1f5f9', margin: 0 } as React.CSSProperties,
  badge: { padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600 } as React.CSSProperties,
  badgeActive: { background: '#10b98133', color: '#34d399' } as React.CSSProperties,
  badgeInactive: { background: '#64748b33', color: '#94a3b8' } as React.CSSProperties,
  statsRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 20 } as React.CSSProperties,
  stat: { background: '#1e293b55', borderRadius: 10, padding: '12px 16px' } as React.CSSProperties,
  statLabel: { fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase' as const, letterSpacing: 0.5, marginBottom: 4 } as React.CSSProperties,
  statValue: { fontSize: 18, fontWeight: 700, color: '#e2e8f0' } as React.CSSProperties,
  actions: { display: 'flex', gap: 10, borderTop: '1px solid #1e293b', paddingTop: 16 } as React.CSSProperties,
  formOverlay: { position: 'fixed' as const, inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' } as React.CSSProperties,
  formCard: { background: '#0f172a', border: '1px solid #1e293b', borderRadius: 16, padding: 32, width: 420, maxWidth: '90vw' } as React.CSSProperties,
  formTitle: { fontSize: 22, fontWeight: 700, marginBottom: 24, color: '#f1f5f9' } as React.CSSProperties,
  label: { display: 'block', fontSize: 13, fontWeight: 600, color: '#94a3b8', marginBottom: 6 } as React.CSSProperties,
  input: { width: '100%', background: '#1e293b', border: '1px solid #334155', color: '#e2e8f0', padding: '12px 16px', borderRadius: 10, fontSize: 15, marginBottom: 16, outline: 'none', boxSizing: 'border-box' as const } as React.CSSProperties,
  formActions: { display: 'flex', gap: 12, marginTop: 8, justifyContent: 'flex-end' } as React.CSSProperties,
  empty: { textAlign: 'center' as const, padding: 80, color: '#64748b' } as React.CSSProperties,
  riskBadge: { background: '#f59e0b22', color: '#f59e0b', padding: '4px 10px', borderRadius: 8, fontSize: 12, fontWeight: 700 } as React.CSSProperties,
};

const Accounts: React.FC = () => {
  const toast = useToast();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editAccount, setEditAccount] = useState<Account | null>(null);
  const [name, setName] = useState('');
  const [balance, setBalance] = useState('10000');
  const [riskPct, setRiskPct] = useState('1');

  useEffect(() => { loadAccounts(); }, []);

  const loadAccounts = async () => {
    try {
      const res = await accountsApi.list();
      setAccounts(res.data);
    } catch (e) { toast.error('Accounts laden fehlgeschlagen', getErrorMessage(e)); }
  };

  const openCreate = () => {
    setEditAccount(null);
    setName('');
    setBalance('10000');
    setRiskPct('1');
    setShowForm(true);
  };

  const openEdit = (acc: Account) => {
    setEditAccount(acc);
    setName(acc.name);
    setBalance(String(acc.balance));
    setRiskPct(String(acc.risk_pct ?? 1));
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!name.trim()) { toast.warning('Name fehlt', 'Bitte gib einen Account-Namen ein.'); return; }
    const risk = parseFloat(riskPct) || 1;

    try {
      if (editAccount) {
        await accountsApi.update(editAccount.id, {
          name: name.trim(),
          balance: parseFloat(balance) || editAccount.balance,
          risk_pct: risk,
        });
        toast.success('Account aktualisiert!', `Risk: ${risk}% pro Trade`);
      } else {
        await accountsApi.create({
          name: name.trim(),
          balance: parseFloat(balance) || 10000,
          risk_pct: risk,
        });
        toast.success('Account erstellt!', `"${name.trim()}" mit ${risk}% Risk pro Trade.`);
      }
      setShowForm(false);
      loadAccounts();
    } catch (err) { toast.error('Speichern fehlgeschlagen', getErrorMessage(err)); }
  };

  const handleActivate = async (id: string) => {
    try {
      await accountsApi.activate(id);
      toast.success('Account activated!');
      loadAccounts();
    } catch (err) { toast.error('Activierung fehlgeschlagen', getErrorMessage(err)); }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Account wirklich löschen?')) return;
    try {
      await accountsApi.delete(id);
      toast.success('Account deleted');
      loadAccounts();
    } catch (err) { toast.error('Delete fehlgeschlagen', getErrorMessage(err)); }
  };

  const activeCount = accounts.filter(a => a.is_active).length;
  const totalBalance = accounts.reduce((sum, a) => sum + a.balance, 0);

  return (
    <div style={s.page}>
      <div style={s.header}>
        <h1 style={s.title}>
          <span style={{ fontSize: 32 }}>🏦</span> Accounts
          <span style={{ fontSize: 14, fontWeight: 400, color: '#64748b', marginLeft: 8 }}>
            {accounts.length} Konten
          </span>
        </h1>
        <button style={s.btnPrimary} onClick={openCreate}>+ New Account</button>
      </div>

      {accounts.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16, marginBottom: 28 }}>
          <div style={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 12, padding: '16px 20px' }}>
            <div style={s.statLabel}>GESAMT-KONTEN</div>
            <div style={s.statValue}>{accounts.length}</div>
          </div>
          <div style={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 12, padding: '16px 20px' }}>
            <div style={s.statLabel}>AKTIV</div>
            <div style={{ ...s.statValue, color: '#34d399' }}>{activeCount}</div>
          </div>
          <div style={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 12, padding: '16px 20px' }}>
            <div style={s.statLabel}>GESAMT-BALANCE</div>
            <div style={{ ...s.statValue, color: totalBalance >= 0 ? '#34d399' : '#ef4444' }}>
              ${totalBalance.toLocaleString('de-DE', { minimumFractionDigits: 2 })}
            </div>
          </div>
        </div>
      )}

      {accounts.length === 0 ? (
        <div style={s.empty}>
          <div style={{ fontSize: 56, marginBottom: 16, opacity: 0.6 }}>🏦</div>
          <h3 style={{ color: '#94a3b8', marginBottom: 8 }}>Keine Konten vorhanden</h3>
          <p style={{ fontSize: 14, marginBottom: 20 }}>Erstelle dein erstes Trading-Konto um loszulegen.</p>
          <button style={s.btnPrimary} onClick={openCreate}>+ Erstes Konto erstellen</button>
        </div>
      ) : (
        <div style={s.grid}>
          {accounts.map(acc => (
            <div key={acc.id} style={{ ...s.card, ...(acc.is_active ? s.cardActive : {}) }}>
              {acc.is_active && (
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'linear-gradient(90deg, #8b5cf6, #6d28d9)' }} />
              )}
              <div style={s.cardHeader}>
                <div>
                  <h3 style={s.accName}>{acc.name}</h3>
                  <span style={{ fontSize: 12, color: '#64748b' }}>ID: {acc.id.substring(0, 8)}...</span>
                </div>
                <span style={{ ...s.badge, ...(acc.is_active ? s.badgeActive : s.badgeInactive) }}>
                  {acc.is_active ? '● Active' : '○ Inaktiv'}
                </span>
              </div>
              <div style={s.statsRow}>
                <div style={s.stat}>
                  <div style={s.statLabel}>BALANCE</div>
                  <div style={{ ...s.statValue, color: acc.balance >= 0 ? '#34d399' : '#ef4444' }}>
                    ${acc.balance.toLocaleString('de-DE', { minimumFractionDigits: 2 })}
                  </div>
                </div>
                <div style={s.stat}>
                  <div style={s.statLabel}>RISK / TRADE</div>
                  <div style={{ ...s.statValue, color: '#f59e0b' }}>
                    {acc.risk_pct ?? 1}%
                  </div>
                  <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                    = ${((acc.balance * (acc.risk_pct ?? 1)) / 100).toFixed(2)}
                  </div>
                </div>
                <div style={s.stat}>
                  <div style={s.statLabel}>ERSTELLT</div>
                  <div style={{ ...s.statValue, fontSize: 14 }}>
                    {acc.created_at ? new Date(acc.created_at).toLocaleDateString('de-DE') : '–'}
                  </div>
                </div>
              </div>
              <div style={s.actions}>
                {!acc.is_active ? (
                  <button style={s.btnActivate} onClick={() => handleActivate(acc.id)}>✅ Activate</button>
                ) : (
                  <span style={{ ...s.btnSecondary, cursor: 'default', opacity: 0.6 }}>✅ Aktiv</span>
                )}
                <button style={s.btnEdit} onClick={() => openEdit(acc)}>✏️ Edit</button>
                <button style={s.btnDanger} onClick={() => handleDelete(acc.id)}>🗑️ Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div className="modal-overlay" style={s.formOverlay} onClick={(e) => { if (e.target === e.currentTarget) setShowForm(false); }}>
          <div className="modal-card" style={s.formCard}>
            <h2 style={s.formTitle}>{editAccount ? '✏️ Account bearbeiten' : '🏦 Neues Trading-Konto'}</h2>

            <label style={s.label}>Account Name *</label>
            <input style={s.input} type="text" placeholder="z.B. BTC, Demo, Futures..."
              value={name} onChange={e => setName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleSave(); }}
              autoFocus />

            <label style={s.label}>Start-Kapital ($)</label>
            <input style={s.input} type="number" placeholder="10000"
              value={balance} onChange={e => setBalance(e.target.value)} />

            <label style={{ ...s.label, color: '#f59e0b' }}>⚡ Risk per Trade (%)</label>
            <input style={{ ...s.input, borderColor: '#f59e0b44' }} type="number" step="0.1" placeholder="1"
              value={riskPct} onChange={e => setRiskPct(e.target.value)} />
            <div style={{ color: '#6b7280', fontSize: 12, marginTop: -10, marginBottom: 16 }}>
              {parseFloat(balance) > 0 && parseFloat(riskPct) > 0 && (
                <>= ${((parseFloat(balance) * parseFloat(riskPct)) / 100).toFixed(2)} Risk pro Trade</>
              )}
            </div>

            <div style={s.formActions}>
              <button style={s.btnSecondary} onClick={() => setShowForm(false)}>Cancel</button>
              <button style={s.btnPrimary} onClick={handleSave}>
                💾 {editAccount ? 'Speichern' : 'Konto erstellen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Accounts;
